/**
 * When the chat drawer's slide is running.
 *
 * The drawer's width transition IS the slide, so the element's own transition
 * events scope it exactly — far better than a timer, because a heavy first
 * paint can push the real start well past the commit that posed it.
 *
 * Two things make that stream ambiguous, and this module answers both:
 * descendants ease widths of their own and their events bubble through the
 * wrap, and a slide can end after a newer one has already begun.
 */

import { STAGE_ANIMATION_MARGIN_MS } from '../stageMotion';

/**
 * Slide duration. Matches .panel's own 200ms edge easing in panels.css, so the
 * stage panels being pushed aside travel with the drawer rather than trailing
 * behind it. The drawer hands it to CSS as --chat-slide-ms.
 */
export const CHAT_SLIDE_MS = 200;

/**
 * How long the stage treats the slide as live: the ease plus the margin every
 * stage animation carries, since a transition can end a frame or two late under
 * load. The window the drawer is kept mounted for after a close, too — its exit
 * is the same slide.
 */
export const CHAT_SLIDE_WINDOW_MS = CHAT_SLIDE_MS + STAGE_ANIMATION_MARGIN_MS;

/**
 * The beat a slide's end waits past transitionend. The frame that ends the
 * slide can also carry the final ResizeObserver reallocation, and reporting
 * the drawer settled in that same commit re-enables panel-edge easing for the
 * last sample — a soft rubber tail right at the finish.
 */
export const SLIDE_END_DEFER_MS = 60;

/** The slide is the watched element easing its own width, nothing else. */
export function isSlideTransition(
    event: { propertyName: string; target: EventTarget | null },
    element: EventTarget | null,
): boolean {
    return element !== null && event.target === element && event.propertyName === 'width';
}

/**
 * Which slide is the newest. A deferred end carries the sequence it was
 * scheduled under and lands only while that is still the current one, so
 * re-opening mid-close cannot report the drawer settled under the open.
 */
export function createSlideSequence(): {
    begin: () => void;
    getCurrent: () => number;
    isCurrent: (seq: number) => boolean;
} {
    let seq = 0;
    return {
        begin: (): void => {
            seq += 1;
        },
        getCurrent: (): number => seq,
        isCurrent: (scheduledValue: number): boolean => scheduledValue === seq,
    };
}
